import { CommonState, MultiProductState } from 'embed/reducers'
import { ApplicationState } from 'embed/reducers/application'
import { ApplicationFields, Field, FieldData, FieldDataValue } from 'embed/constants'
import { QuestionMachineConfig } from 'embed/constants/life-insurance'
import { SUMMARY_FIELDS, SummaryFieldType } from 'embed/constants/fields'
import { filtersCreditRedemptionTypesSelector } from 'embed/selectors/filters'
import { GenderTypeOptions, HeightTypeOptions, LoanPurposeOptions, Product } from 'even-ts-static'
import { createSelector } from 'reselect'
import { Auto, BasicLoan, SLR } from 'embed/components/Loan/Application/loan-variant'

export const applicationStateSelector = (state: CommonState): ApplicationState => state.application
export const applicationCreditCardFieldsSelector = (state: CommonState): ApplicationFields | undefined =>
  applicationStateSelector(state).credit_card?.fields
export const applicationCreditCardFirstNameSelector = (state: CommonState): FieldDataValue | undefined =>
  applicationCreditCardFieldsSelector(state)?.firstName?.value
export const applicationCreditCardConsentsToFCRAFieldSelector = (state: CommonState): FieldData | undefined =>
  applicationCreditCardFieldsSelector(state)?.consentsToFcra
export const applicationCreditCardConsentsToTCPAFieldSelector = (state: CommonState): FieldData | undefined =>
  applicationCreditCardFieldsSelector(state)?.consentsToTcpa
export const applicationCreditCardRewardsPreferenceFieldSelector = (state: CommonState): FieldData | undefined =>
  applicationCreditCardFieldsSelector(state)?.rewardsPreference
export const applicationCreditCardRewardsPreferenceValueSelector = (state: CommonState): FieldDataValue | undefined =>
  applicationCreditCardRewardsPreferenceFieldSelector(state)?.value
// TODO make Offer augmenter return enums
export const creditCardApplicationRewardsPreferenceUuidSelector = createSelector(
  [applicationCreditCardRewardsPreferenceValueSelector, filtersCreditRedemptionTypesSelector],
  (rewardsPreference?: string, redemptionTypes?) => {
    if (rewardsPreference && redemptionTypes?.hasOwnProperty(rewardsPreference)) {
      return redemptionTypes[rewardsPreference]
    }
    return undefined
  },
)
export const applicationCreditCardZipCodeFieldSelector = (state: CommonState): FieldData | undefined =>
  applicationCreditCardFieldsSelector(state)?.zipcode
export const lifeInsuranceApplicationSelector = (state: CommonState) => applicationStateSelector(state).life_insurance
export const lifeInsuranceApplicationFieldsSelector = (state: CommonState): ApplicationFields | undefined =>
  lifeInsuranceApplicationSelector(state).fields
export const lifeInsuranceApplicationEmailFieldSelector = (state: CommonState) =>
  lifeInsuranceApplicationFieldsSelector(state)?.email
export const lifeInsuranceApplicationGenderFieldSelector = (state: CommonState) =>
  lifeInsuranceApplicationFieldsSelector(state)?.gender
export const lifeInsuranceApplicationGenderSelector = (state: CommonState) =>
  lifeInsuranceApplicationGenderFieldSelector(state)?.value as GenderTypeOptions
export const lifeInsuranceApplicationHeightInInchesFieldSelector = (state: CommonState) =>
  lifeInsuranceApplicationFieldsSelector(state)?.heightInInches
export const lifeInsuranceApplicationHeightInInchesSelector = (state: CommonState) =>
  lifeInsuranceApplicationHeightInInchesFieldSelector(state)?.value as HeightTypeOptions
export const lifeInsuranceApplicationStateOfResidenceSelector = (state: CommonState) =>
  lifeInsuranceApplicationFieldsSelector(state)?.state?.value
export const applicationCreditCardPurposesFieldSelector = (state: CommonState): FieldData | undefined =>
  applicationCreditCardFieldsSelector(state)?.cardPurposes
export const loanApplicationSelector = (state: CommonState) => applicationStateSelector(state).loan
export const loanApplicationFieldsSelector = (state: CommonState): ApplicationFields | undefined =>
  loanApplicationSelector(state)?.fields
export const loanApplicationFirstNameSelector = (state: CommonState) =>
  loanApplicationFieldsSelector(state)?.firstName?.value
export const loanApplicationPurposeFieldSelector = (state: MultiProductState): FieldData | undefined =>
  loanApplicationFieldsSelector(state)?.purpose
export const loanPurposeIsAutomobileRefinanceSelector = (state: CommonState): boolean =>
  loanApplicationFieldsSelector(state)?.purpose?.value === LoanPurposeOptions.AUTO_REFINANCE
export const loanPurposeIsSLRSelector = (state: CommonState): boolean =>
  loanApplicationFieldsSelector(state)?.purpose?.value === LoanPurposeOptions.STUDENT_REFINANCE
export const loanHasCoApplicantFieldSelector = (state: CommonState): FieldData | undefined =>
  loanApplicationFieldsSelector(state)?.hasCoApplicant
export const loanEmailSelector = (state: CommonState) => loanApplicationFieldsSelector(state)?.email?.value
export const loanHasCoApplicantSelector = (state: CommonState): boolean =>
  loanHasCoApplicantFieldSelector(state)?.value === true
export const loanVariantSelector = createSelector(
  [loanPurposeIsSLRSelector, loanPurposeIsAutomobileRefinanceSelector],
  (loanPurposeIsSLR, loanPurposeIsAutomobileRefinance) => {
    if (loanPurposeIsSLR) {
      return SLR()
    } else if (loanPurposeIsAutomobileRefinance) {
      return Auto()
    } else {
      return BasicLoan()
    }
  },
)
export const applicationCreditCardAnnualIncomeFieldSelector = (state: CommonState): FieldData | undefined =>
  applicationCreditCardFieldsSelector(state)?.annualIncome
export const applicationCreditCardConsentSelector = createSelector(
  [applicationCreditCardConsentsToFCRAFieldSelector, applicationCreditCardConsentsToTCPAFieldSelector],
  (fcraField: FieldData, tcpaField: FieldData) => (fcraField?.value as boolean) && (tcpaField?.value as boolean),
)
export const applicationLifeInsuranceRouterQuestionsSelector = (state: CommonState) =>
  applicationStateSelector(state).life_insurance.routerQuestions
export const applicationLifeInsuranceRouterQuestionsQuestionsSelector = (state: CommonState) =>
  applicationLifeInsuranceRouterQuestionsSelector(state)?.questions ?? {}
export const applicationLifeInsuranceRouterQuestionsGroupsSelector = (state: CommonState) =>
  applicationLifeInsuranceRouterQuestionsSelector(state)?.groups ?? []
export const applicationLifeInsuranceRouterQuestionsMachineConfigSelector = (state: CommonState) =>
  applicationLifeInsuranceRouterQuestionsSelector(state)?.machineConfig as QuestionMachineConfig
export const applicationLifeInsurancePersonalInformationIsValidSelector = (state: CommonState) => {
  const applicationFields = lifeInsuranceApplicationFieldsSelector(state)
  return [
    applicationFields?.firstName,
    applicationFields?.lastName,
    applicationFields?.email,
    applicationFields?.primaryPhone,
  ].reduce((validSoFar, field) => validSoFar && field !== undefined && field.isValid, true)
}
export const applicationMortgageSelector = (state: CommonState) => applicationStateSelector(state)[Product.Mortgage]

export const summaryFieldsToDisplay = createSelector([loanApplicationFieldsSelector], (fieldsData): Field[] => {
  const fieldsToDisplay: Field[] = []

  // Determine which of the summary fields should be displayed
  SUMMARY_FIELDS.forEach((summaryField) => {
    if (summaryField.type === SummaryFieldType.SummaryFieldGroup) {
      // Summary Field Groups should only display their fields
      // if all required fields were pre-populated by a data source (Plaid, GeoIP, etc)
      const allRequiredFieldsArePresent = summaryField.fields?.every(
        (field) => !field.options.required || fieldsData?.[field.key]?.dataSource !== undefined,
      )
      if (allRequiredFieldsArePresent && Array.isArray(summaryField.fields)) {
        fieldsToDisplay.push(...summaryField.fields)
      }
    } else if (summaryField.type === SummaryFieldType.SummaryField) {
      // Summary Fields should show up as it was pre-populated by a data source (Plaid, GeoIP, etc)
      if (summaryField.field !== undefined && fieldsData?.[summaryField.field.key]?.dataSource !== undefined) {
        fieldsToDisplay.push(summaryField.field)
      }
    }
  })

  return fieldsToDisplay
})
